import { useRef, useState } from "react";

/**
 * A ref that is also a callback, so it works both as `<div ref={ref} />` and as a target callers
 * assign into (`ref.current = element`). Either path notifies the owning hook, which is what lets an
 * observer re-bind when the observed element is swapped.
 */
export interface ObservedElementRef<T extends HTMLElement> {
  (element: T | null): void;
  current: T | null;
}

export interface UseObservedElementRefResult<T extends HTMLElement> {
  /** Attach to an element, or assign into, to observe it. */
  ref: ObservedElementRef<T>;
  /** The attached element, as state — so effects can depend on it and re-subscribe on a swap. */
  element: T | null;
}

/**
 * Tracks the currently-attached element by *identity*, as render-visible state.
 *
 * This is the piece that makes "observe an element" work across an element swap. A plain `useRef`
 * cannot: mutating `.current` triggers no render, so an effect keyed on it never re-subscribes and
 * keeps observing a detached node. Reading the ref during render instead — polling `.current` on every
 * pass — is the other tempting workaround, and it costs a render-phase effect with no dependency array
 * plus an extra commit on mount.
 *
 * The element is deliberately held twice. The ref is the synchronous truth, so `ref.current` reads back
 * the node the caller just assigned within the same tick. The state copy is what makes the swap
 * observable to the render cycle.
 *
 * `ref` is stable for the lifetime of the component, so it is safe as an effect dependency and can be
 * handed to a child or through context without re-subscribing anything.
 */
export const useObservedElementRef = <T extends HTMLElement = HTMLElement>(): UseObservedElementRefResult<T> => {
  const elementRef = useRef<T | null>(null);
  const [element, setElement] = useState<T | null>(null);

  const refRef = useRef<ObservedElementRef<T> | null>(null);
  if (!refRef.current) {
    const attach = (next: T | null) => {
      if (elementRef.current === next) return;
      elementRef.current = next;
      setElement(next);
    };

    // Declared with the full ref type, then given `current` as an accessor so reads stay live against
    // `elementRef`. A plain property would capture whatever the node was when this object was built —
    // `null` — and never see the attach.
    const observedRef: ObservedElementRef<T> = Object.assign(attach, { current: null });
    Object.defineProperty(observedRef, "current", {
      get: () => elementRef.current,
      set: attach,
    });

    refRef.current = observedRef;
  }

  return { ref: refRef.current, element };
};
